import { useDispatch, useSelector } from 'react-redux'
import {
  selectCustomerSubscriptionData,
  selectEnergyCharacteristicsSubscriptionData,
  selectEnergyContractSubscriptionData,
  selectGasMeterSubscriptionData,
  selectTelecomCharacteristicsSubscriptionData,
  selectTelecomContractSubscriptionData,
} from '@/containers/SubscriptionFormPage/SubscriptionFormSelectors'

import {
  selectCommonFormConfigurationData,
  selectOfferModuleConfigurationData,
} from '@/containers/SubscriptionFormPage/SubscriptionFormConfigurationSelectors'
import { useTranslation } from 'react-i18next'
import { clubSelectraModuleActions } from '@/containers/SubscriptionFormPageNew/Components/ClubSelectra/ClubSelectraSagas'
import {
  selectActiveTabType,
  selectCharacteristicsCurrentContractActive,
  selectClubSelectraState,
  selectConfig,
  selectConfigCurrentContractActive,
  selectContractDetailsCurrentContractActive,
  selectGasCurrentContractActive,
  selectOfferConfigCurrentContractActive,
  selectPersonalContactSubscriptionData,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSelectors'
import { useSnackbar } from 'baseui/snackbar'
import useBusinessType from '../../../../shared/hooks/useBusinessType'
import {
  BUSINESS_VERTICAL_ENERGY,
  BUSINESS_VERTICAL_INSURANCE,
  BUSINESS_VERTICAL_TELECOM,
} from '../../SubscriptionForm/Common/constants'
import { selectOfferConfiguration } from '../../../SubscriptionFormPage/SubscriptionFormConfigurationSelectors'
import { selectAuthData } from '../../../Login/AuthSelectors'

const useClubSelectra = ({ version, module }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { enqueue } = useSnackbar()
  const { typeTranslated: legacyApplicationType } = useBusinessType({
    loaderRoutesEnergy: {},
    loaderRoutesInsurance: {},
    loaderRoutes: {},
    modulesStatuses: {},
    modulesStatusesEnergy: {},
    modulesStatusesInsurance: {},
  })
  const { country } = useSelector(selectAuthData)
  const isV2 = version !== 'legacy'

  const v2ApplicationType = useSelector(selectActiveTabType)
  const legacyEnergyCharacteristics = useSelector(selectEnergyCharacteristicsSubscriptionData)
  const legacyTelecomCharacteristics = useSelector(selectTelecomCharacteristicsSubscriptionData)
  const legacyEnergyContract = useSelector(selectEnergyContractSubscriptionData)
  const legacyTelecomContract = useSelector(selectTelecomContractSubscriptionData)
  const legacyGasMeter = useSelector(selectGasMeterSubscriptionData)
  const legacyOfferModuleConfig = useSelector(selectOfferModuleConfigurationData)
  const legacyOfferConfig = useSelector(selectOfferConfiguration)
  const legacyCustomerData = useSelector(selectCustomerSubscriptionData)
  const legacyCommonFormConfig = useSelector(selectCommonFormConfigurationData)

  const v2Characteristics = useSelector(selectCharacteristicsCurrentContractActive)
  const v2ContractDetails = useSelector(selectContractDetailsCurrentContractActive)
  const v2GasData = useSelector(selectGasCurrentContractActive)
  const v2ContractConfig = useSelector(selectConfigCurrentContractActive)
  const v2OfferConfig = useSelector(selectOfferConfigCurrentContractActive)
  const v2FormConfig = useSelector(selectConfig)

  const clubSelectra = useSelector(selectClubSelectraState)
  const v2PersonalData = useSelector(selectPersonalContactSubscriptionData)

  const contractApplicationType = isV2 ? v2ApplicationType : legacyApplicationType
  const contractType = (contractApplicationType || '').toLowerCase()
  const isEnergyBusinessVertical = contractType === BUSINESS_VERTICAL_ENERGY

  const energyType = isV2 ? v2Characteristics.energyType : legacyEnergyCharacteristics.energyType
  const energyPrestationType = isV2 ? v2Characteristics.prestationType : legacyEnergyCharacteristics.prestationType
  const telecomPrestationType = isV2 ? v2Characteristics.prestationType : legacyTelecomCharacteristics.prestationType
  const productType = isV2 ? v2Characteristics.productType : legacyTelecomCharacteristics.productType

  const legacyContract = isEnergyBusinessVertical ? legacyEnergyContract : legacyTelecomContract
  const offerId = isV2 ? v2ContractDetails.offerId : legacyContract.offerId
  const providerExternalId = isV2 ? v2ContractDetails.providerExternalId : legacyContract.providerExternalId
  const providerExternalIdGas = isV2 ? v2GasData.providerExternalId : legacyGasMeter.providerExternalIdGas

  const offers = isV2 ? v2ContractConfig.offers : legacyOfferModuleConfig.offers
  const offerName = isV2 ? v2OfferConfig.name : legacyOfferConfig.name
  const organization = isV2 ? v2OfferConfig.organization : legacyOfferConfig.organization

  const isClubSelectraRegistered = isV2 ? clubSelectra.success === true : legacyCustomerData.isClubSelectraRegistered
  const isLoading = clubSelectra.loading
  const success = clubSelectra.success
  const message = clubSelectra.message || ''
  const error = clubSelectra.error || ''

  const clientId = isV2 ? v2FormConfig?.contactId : legacyCommonFormConfig?.id
  const client = isV2 ? v2PersonalData : legacyCustomerData
  const { zohoId } = client || ''
  const contactFirstName = isV2 ? (client?.firstName ?? '') : (client?.name ?? '')
  const contactLastName = isV2 ? (client?.lastName ?? '') : (client?.surname ?? '')
  const contactEmail = client?.email ?? ''

  const createClubSelectra = () => {
    if (isLoading) return

    dispatch({
      type: clubSelectraModuleActions.CREATE_CLUB_SELECTRA,
      enqueue,
      payload: buildPayload(),
      version,
    })
  }

  const isEmptyValue = value => !value || String(value).trim() === ''

  const isContactModule = module === 'contact'

  const baseMandatoryEntries = [
    [t('subscriptionForm:customer_module.firstname.label'), contactFirstName],
    [t('subscriptionForm:customer_module.lastname.label'), contactLastName],
    [t('subscriptionForm:customer_module.email.label'), contactEmail],
    [t('subscriptionForm:zoho_module.module_title'), zohoId],
    ['Subscription id', clientId],
  ]

  const mandatoryEnergyFields = [[t('subscriptionForm:characteristics_module.energy_type.label'), energyType]]

  const mandatoryTelecomFields = [[t('subscriptionForm:characteristics_module.product_type.label'), productType]]

  const extraMandatoryFields = isEnergyBusinessVertical ? mandatoryEnergyFields : mandatoryTelecomFields
  const mandatoryEntries = [
    ...baseMandatoryEntries,
    ...(!isContactModule
      ? [...extraMandatoryFields, [t('subscriptionForm:characteristics_module.contract_type.label'), offerName]]
      : []),
  ]
  const currentMissingLabels = mandatoryEntries.filter(([, value]) => isEmptyValue(value)).map(([label]) => label)
  const labelsToShow = !isClubSelectraRegistered ? currentMissingLabels : []
  const prestationType = isEnergyBusinessVertical ? energyPrestationType : telecomPrestationType

  const buildPayload = () => {
    const payload = {
      contact_zoho_id: zohoId,
      contact_first_name: contactFirstName,
      contact_last_name: contactLastName,
      country,
      contact_email: contactEmail,
      client_id: clientId,
      version,
      offer_name: offerName ?? '',
      business_vertical: contractType,
      prestation_type: prestationType ?? '',
      provider_zoho_prefix: organization?.zoho_prefix ?? '',
    }

    if (contractType === BUSINESS_VERTICAL_TELECOM && productType) {
      payload.product_type = productType
    }

    if (isEnergyBusinessVertical && energyType) {
      payload.energy_type = energyType
    }

    if ((energyType === 'elec' || energyType === 'dual') && providerExternalId) {
      payload.provider_external_id_elec = providerExternalId
    }

    if ((energyType === 'gas' || energyType === 'dual') && providerExternalIdGas) {
      payload.provider_external_id_gas = providerExternalIdGas
    }

    return payload
  }

  const disabled =
    contractType === BUSINESS_VERTICAL_INSURANCE ||
    isLoading ||
    isClubSelectraRegistered === true ||
    currentMissingLabels.length > 0 ||
    success === true

  return {
    t,
    isLoading,
    success,
    message,
    error,
    isClubSelectraRegistered,
    energyType,
    offerId,
    offers,
    zohoId,
    clientId,
    contactFirstName,
    contactLastName,
    contactEmail,
    offerName,
    labelsToShow:
      contractType === BUSINESS_VERTICAL_INSURANCE
        ? [t('subscriptionForm:header.modal_save_send.club_selectra_no_vertical_integrated')]
        : labelsToShow,
    currentMissingLabels,
    disabled,
    createClubSelectra,
    isContactModule,
  }
}

export default useClubSelectra
